interface SnippetSetResponse {
  success: boolean;
  data: {
    id: string;
    name: string;
    iso: string;
    baseFile: string;
    snippets: Record<string, string>; // Object with translationKey as key and value as value
  };
}

/**
 * Composable to fetch and manage snippets from Shopware
 * Uses Nitro cache via server API route
 * @public
 */
export const useSnippets = () => {
  const { locale } = useI18n();
  const isLoadingSnippets = ref(false);
  const snippetData = useState<SnippetSetResponse["data"] | null>("snippetData", () => null);

  /**
   * Fetch snippets from the API (uses Nitro cache)
   * @param localeCode - Optional locale code, defaults to current locale
   */
  const fetchSnippetSet = async (localeCode?: string): Promise<SnippetSetResponse["data"] | null> => {
    const currentLocale = localeCode || locale.value || 'de-DE';
    
    // Check state first (client-side cache)
    if (snippetData.value && snippetData.value.iso === currentLocale && snippetData.value.snippets && typeof snippetData.value.snippets === 'object') {
      return snippetData.value;
    }

    isLoadingSnippets.value = true;
    try {
      const data = await $fetch<SnippetSetResponse["data"]>(
        `/api/snippets/${currentLocale}`
      );

      if (data && data.snippets && typeof data.snippets === 'object') {
        snippetData.value = data;
        return data;
      }
      
      return null;
    } catch (error) {
      return null;
    } finally {
      isLoadingSnippets.value = false;
    }
  };

  /**
   * Get a snippet value by translation key
   * @param translationKey - The translation key to look up (without prefix)
   * @param localeCode - Optional locale code
   */
  const getSnippet = async (translationKey: string, localeCode?: string): Promise<string | null> => {
    const data = await fetchSnippetSet(localeCode);
    if (!data?.snippets || typeof data.snippets !== 'object') return null;
    
    return data.snippets[translationKey] || null;
  };

  /**
   * Get all snippets as an object
   * @param localeCode - Optional locale code
   */
  const getAllSnippets = async (localeCode?: string): Promise<Record<string, string>> => {
    const data = await fetchSnippetSet(localeCode);
    if (!data?.snippets || typeof data.snippets !== 'object') return {};
    
    return data.snippets;
  };



  return {
    snippetData: computed(() => snippetData.value),
    isLoadingSnippets: computed(() => isLoadingSnippets.value),
    fetchSnippetSet,
    getSnippet,
    getAllSnippets,
  };
};

